import { Fragment, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DotLoader } from 'react-spinners';
import Cookies from 'js-cookie';
import {
  ImagePlus, FileImage, Youtube, Glasses,
  AlignLeft, Tag, Layers, BedDouble, Bath, Ruler,
  DollarSign, User, Phone, MessageSquare,
  MapPin, ChevronRight, CheckCircle2,
  MessageCircle, MessageCircleOff,
} from 'lucide-react';
import { useEditPropertyForm } from '../../hooks/useEditPropertyForm';
import ImagesUploadEdit from '../shared/ImagesUploadEdit';
import GetLatLngForUpload from '../shared/LocationPicker';
import {
  OWNER_OPTIONS, FURNITURE_OPTIONS, makeFirstHandOptions, ROOM_OPTIONS, AMENITY_OPTIONS,
  FormSection, UnitToggle, BinaryToggle, FormInput, FormButton, ErrorBanner, SuccessBanner,
} from './shared/FormComponents';

const STEPS = [
  { step: 1, label: 'รูปภาพ' },
  { step: 2, label: 'รายละเอียด' },
  { step: 3, label: 'ข้อมูล' },
  { step: 4, label: 'ตำแหน่ง' },
];

function StepBar({ current, onGoTo }) {
  return (
    <div className="flex items-center w-full mt-5 mb-1">
      {STEPS.map(({ step, label }, i) => {
        const done   = current > step;
        const active = current === step;
        return (
          // key ต้องอยู่บนตัวนอกสุดของแต่ละรอบ (fragment) ไม่ใช่บน <button> ข้างใน
          <Fragment key={step}>
            <button
              type="button"
              onClick={() => onGoTo(step)}
              className="flex flex-col items-center gap-1 w-14 flex-shrink-0"
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                done   ? 'bg-postperty text-white'
                : active ? 'bg-postperty text-white ring-4 ring-postperty/20'
                : 'bg-gray-100 text-gray-400'
              }`}>
                {done ? <CheckCircle2 size={16} /> : step}
              </div>
              <span className={`text-xs whitespace-nowrap ${active ? 'text-postperty font-medium' : 'text-gray-400'}`}>
                {label}
              </span>
            </button>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mb-4 rounded-full transition-colors ${done ? 'bg-postperty' : 'bg-gray-100'}`} />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

export default function EditPropertyForm({ config }) {
  const navigate = useNavigate();

  const {
    upload, setUpload, setPageNumber,
    allowComment, setAllowComment,
    isLoadingOldData, isUpdating,
    field, setField, toggleArrayField,
    handleDetailPost, handlePrice,
    pageNumber, goToPage, nextPage,
    detailLength,
    formError, submitError, submitSuccess,
    handleSubmit,
  } = useEditPropertyForm(config);

  const {
    images, front_deeds, back_deeds,
    youtube_url, vr_url,
    heading_post, detail_post, type_post,
    floor, area, unit_area, use_area, use_unit_area,
    bedroom, bathroom,
    rai, ngan, wah,
    price, rent_price, sellout_price,
    owner_status, first_hand, furniture_included,
    phone_number, line_id, excursive_option,
    asset_location_latitude, asset_location_longitude,
  } = upload;

  useEffect(() => {
    if (!Cookies.get('jwt')) navigate('/login', { replace: true });
  }, [navigate]);

  const handleDeedChange = (name) => (e) =>
    setUpload((prev) => ({ ...prev, [name]: Array.from(e.target.files) }));

  const fieldErr = (name) =>
    formError?.field === name ? formError.message : undefined;

  if (isLoadingOldData) {
    return (
      <div className="w-full h-screen flex justify-center items-center">
        <DotLoader color="#34bfd9" size={60} />
      </div>
    );
  }

  // ── Page 1: Media ──────────────────────────────────────────────────────────
  const renderPage1 = () => (
    <div className="flex flex-col gap-3 pb-10">

      <FormSection error={fieldErr('images')}>
        <div
          onClick={() => setPageNumber(1.5)}
          className={`relative h-52 md:h-64 rounded-xl overflow-hidden border-2 border-dashed bg-gray-50 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-postperty hover:bg-postperty/5 transition-all group ${
            fieldErr('images') ? 'border-red-300' : 'border-gray-200'
          }`}
        >
          {images.length > 0 ? (
            <>
              <img
                src={typeof images[0] === 'string' ? images[0] : URL.createObjectURL(images[0])}
                alt="preview"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <ImagePlus size={28} className="text-white" />
              </div>
              <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2.5 py-1 rounded-full">
                {images.length} รูป
              </span>
            </>
          ) : (
            <>
              <ImagePlus size={32} className="text-gray-300" />
              <p className="text-sm text-gray-400">แตะเพื่อแก้ไขรูปภาพ</p>
            </>
          )}
        </div>
      </FormSection>

      {config.hasDeeds && (
        <FormSection title="โฉนดที่ดิน">
          <div className="grid grid-cols-2 gap-3">
            {[
              { name: 'front_deeds', files: front_deeds, label: 'โฉนดด้านหน้า' },
              { name: 'back_deeds',  files: back_deeds,  label: 'โฉนดด้านหลัง' },
            ].map(({ name, files, label }) => (
              <label key={name} className="relative h-36 rounded-xl overflow-hidden border-2 border-dashed border-gray-200 bg-gray-50 flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-postperty transition-colors">
                {files.length > 0 ? (
                  <img
                    src={typeof files[files.length - 1] === 'string' ? files[files.length - 1] : URL.createObjectURL(files[files.length - 1])}
                    alt={label}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <>
                    <FileImage size={22} className="text-gray-300" />
                    <span className="text-xs text-gray-400">{label}</span>
                  </>
                )}
                <input type="file" className="hidden" accept="image/png, image/jpeg, image/jpg" onChange={handleDeedChange(name)} multiple />
              </label>
            ))}
          </div>
        </FormSection>
      )}

      <FormSection title="ลิงก์วิดีโอ / VR (ไม่บังคับ)" error={fieldErr('youtube_url') || fieldErr('vr_url')}>
        <div className="flex flex-col gap-2">
          <div className="relative">
            <Youtube size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <FormInput defaultValue={youtube_url} onChange={field('youtube_url')} type="text" placeholder="YouTube URL" className="pl-9" />
          </div>
          <div className="relative">
            <Glasses size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <FormInput defaultValue={vr_url} onChange={field('vr_url')} type="text" placeholder="VR URL (Matterport, Cloudpano, Kuula, Pano360)" className="pl-9" />
          </div>
        </div>
      </FormSection>

      <FormButton onClick={nextPage}>
        ถัดไป <ChevronRight size={16} />
      </FormButton>
    </div>
  );

  // ── Page 2: Details + pricing ──────────────────────────────────────────────
  const renderPage2 = () => (
    <div className="flex flex-col gap-3 pb-10">

      <FormSection title="หัวข้อประกาศ" required error={fieldErr('heading_post')}>
        <div className="relative">
          <Tag size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <FormInput defaultValue={heading_post} onChange={field('heading_post')} type="text" placeholder={`เช่น ${config.title} 2 ชั้น ย่านลาดพร้าว`} className="pl-9" />
        </div>
      </FormSection>

      <FormSection title={`รายละเอียด (${detailLength}/1800)`} required error={fieldErr('detail_post')}>
        <div className="relative">
          <AlignLeft size={15} className="absolute left-3 top-3 text-gray-400" />
          <textarea
            value={detail_post ?? ''}
            onChange={handleDetailPost}
            placeholder="อธิบายรายละเอียดทรัพย์สิน..."
            className="w-full h-40 bg-gray-50 rounded-xl pl-9 pr-3 pt-3 pb-3 text-sm outline-none border border-gray-100 focus:border-postperty transition-colors resize-none"
          />
        </div>
      </FormSection>

      <FormSection title="ประเภทประกาศ" required error={fieldErr('type_post')}>
        <div className="flex gap-3">
          {[
            { label: 'ขาย', show: true },
            { label: 'เช่า', show: true },
            { label: 'เซ้ง', show: config.hasSellOut },
          ]
            .filter((t) => t.show)
            .map(({ label }) => {
              const active = type_post.includes(label);
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => toggleArrayField('type_post', label)}
                  className={`flex-1 h-11 rounded-xl text-sm font-medium border transition-all ${
                    active
                      ? 'bg-postperty text-white border-postperty shadow-sm'
                      : 'bg-gray-50 text-gray-500 border-gray-100 hover:border-postperty'
                  }`}
                >
                  {label}
                </button>
              );
            })}
        </div>
      </FormSection>

      {config.hasRooms && (
        <FormSection title="จำนวนชั้น" required error={fieldErr('floor')}>
          <div className="relative">
            <Layers size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <FormInput defaultValue={floor} onChange={field('floor')} type="number" min="1" max="200" placeholder="เช่น 2" className="pl-9" />
          </div>
        </FormSection>
      )}

      {config.hasArea && (
        <FormSection title="ขนาดพื้นที่รวม" required error={fieldErr('area') || fieldErr('unit_area')}>
          <div className="relative">
            <Ruler size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <FormInput defaultValue={area} onChange={field('area')} type="number" placeholder="ขนาดพื้นที่" className="pl-9" />
          </div>
          <UnitToggle value={unit_area} onChange={(v) => setField('unit_area', v)} />
        </FormSection>
      )}

      {config.hasUseArea && (
        <FormSection title="พื้นที่ใช้สอย" required error={fieldErr('use_area') || fieldErr('use_unit_area')}>
          <div className="relative">
            <Ruler size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <FormInput value={use_area ?? ''} onChange={field('use_area')} type="number" placeholder="ขนาดพื้นที่ใช้สอย" className="pl-9" />
          </div>
          {!config.defaultUseAreaUnit && (
            <UnitToggle value={use_unit_area} onChange={(v) => setField('use_unit_area', v)} />
          )}
        </FormSection>
      )}

      {config.hasRooms && (
        <FormSection title="ห้องนอน / ห้องน้ำ" required error={fieldErr('bedroom') || fieldErr('bathroom')}>
          <div className="grid grid-cols-2 gap-3">
            {[
              { name: 'bedroom',  label: 'ห้องนอน', value: bedroom,  Icon: BedDouble },
              { name: 'bathroom', label: 'ห้องน้ำ',  value: bathroom, Icon: Bath      },
            ].map(({ name, label, value: val, Icon }) => (
              <div key={name} className="relative">
                <Icon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <select
                  value={val ?? ''}
                  onChange={(e) => setField(name, e.target.value || null)}
                  className="w-full h-12 bg-gray-50 rounded-xl pl-9 pr-3 text-sm outline-none border border-gray-100 focus:border-postperty transition-colors appearance-none"
                >
                  <option value="">{label}</option>
                  {ROOM_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n} {label}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </FormSection>
      )}

      {config.hasLandArea && (
        <FormSection title="ขนาดที่ดิน" required error={fieldErr('land')}>
          <div className="grid grid-cols-3 gap-2">
            {[
              { name: 'rai',  label: 'ไร่',  val: rai  },
              { name: 'ngan', label: 'งาน',  val: ngan },
              { name: 'wah',  label: 'วา',   val: wah  },
            ].map(({ name, label, val }) => (
              <div key={name} className="flex flex-col gap-1">
                <span className="text-xs text-gray-400 text-center">{label}</span>
                <FormInput defaultValue={val} onChange={field(name)} type="number" min="0" placeholder="0" className="text-center px-2" />
              </div>
            ))}
          </div>
        </FormSection>
      )}

      {[
        { label: 'ราคาขาย',  type: 'ขาย',  name: 'price',         value: price         },
        { label: 'ราคาเช่า', type: 'เช่า', name: 'rent_price',    value: rent_price    },
        ...(config.hasSellOut
          ? [{ label: 'ราคาเซ้ง', type: 'เซ้ง', name: 'sellout_price', value: sellout_price }]
          : []),
      ].map(({ label, type, name, value }) =>
        type_post.includes(type) ? (
          <FormSection key={name} title={label} required error={fieldErr(name)}>
            <div className="relative">
              <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <FormInput value={value ?? ''} onChange={handlePrice(name)} type="text" placeholder="0" className="pl-9" />
            </div>
          </FormSection>
        ) : null
      )}

      <FormButton onClick={nextPage}>
        ถัดไป <ChevronRight size={16} />
      </FormButton>
    </div>
  );

  // ── Page 3: Seller info + allowComment ────────────────────────────────────
  const renderPage3 = () => (
    <div className="flex flex-col gap-3 pb-10">

      <FormSection title="สถานะผู้ขาย" required error={fieldErr('owner_status')}>
        <BinaryToggle value={owner_status} options={OWNER_OPTIONS} onChange={(v) => setField('owner_status', v)} />
      </FormSection>

      {config.hasFirstHand && (
        <FormSection title={`สถานะ${config.title}`} required error={fieldErr('first_hand')}>
          <BinaryToggle value={first_hand} options={makeFirstHandOptions(config.title)} onChange={(v) => setField('first_hand', v)} />
        </FormSection>
      )}

      {config.hasFurniture && (
        <FormSection title="เฟอร์นิเจอร์" required error={fieldErr('furniture_included')}>
          <BinaryToggle value={furniture_included} options={FURNITURE_OPTIONS} onChange={(v) => setField('furniture_included', v)} />
        </FormSection>
      )}

      <FormSection title="ข้อมูลติดต่อ" error={fieldErr('phone_number')}>
        <div className="flex flex-col gap-2">
          <div className="relative">
            <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <FormInput id="phoneNumberInput" defaultValue={phone_number} onChange={field('phone_number')} type="text" placeholder="เบอร์โทรศัพท์ (0812345678)" className="pl-9" />
          </div>
          <div className="relative">
            <MessageSquare size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <FormInput defaultValue={line_id} onChange={field('line_id')} type="text" placeholder="Line ID" className="pl-9" />
          </div>
        </div>
      </FormSection>

      <FormSection title="สิ่งอำนวยความสะดวก">
        <div className="grid grid-cols-2 gap-2">
          {AMENITY_OPTIONS.map(({ id, value, Icon }) => {
            const active = excursive_option.includes(value);
            return (
              <button
                key={id}
                type="button"
                onClick={() => toggleArrayField('excursive_option', value)}
                className={`h-11 rounded-xl flex items-center justify-center gap-2 text-sm border transition-all ${
                  active
                    ? 'bg-postperty/10 text-postperty border-postperty/30 font-medium'
                    : 'bg-gray-50 text-gray-500 border-gray-100 hover:border-postperty/30'
                }`}
              >
                <Icon size={15} />
                {value}
              </button>
            );
          })}
        </div>
      </FormSection>

      <FormSection title="อนุญาตให้คอมเมนต์">
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setAllowComment(true)}
            className={`h-11 rounded-xl flex items-center justify-center gap-2 text-sm border transition-all ${
              allowComment
                ? 'bg-green-500 text-white border-green-500 shadow-sm'
                : 'bg-gray-50 text-gray-500 border-gray-100 hover:border-green-400'
            }`}
          >
            <MessageCircle size={15} />
            อนุญาต
          </button>
          <button
            type="button"
            onClick={() => setAllowComment(false)}
            className={`h-11 rounded-xl flex items-center justify-center gap-2 text-sm border transition-all ${
              !allowComment
                ? 'bg-red-400 text-white border-red-400 shadow-sm'
                : 'bg-gray-50 text-gray-500 border-gray-100 hover:border-red-300'
            }`}
          >
            <MessageCircleOff size={15} />
            ไม่อนุญาต
          </button>
        </div>
      </FormSection>

      <FormButton onClick={nextPage}>
        ยืนยัน <ChevronRight size={16} />
      </FormButton>
    </div>
  );

  // ── Page 4: Location ───────────────────────────────────────────────────────
  const renderPage4 = () => (
    <div className="flex flex-col gap-3 pb-10">

      <FormSection title="ปักหมุดตำแหน่งทรัพย์" required error={fieldErr('location')}>
        <div className="w-full h-[380px] md:h-[460px] rounded-xl overflow-hidden border border-gray-100">
          <GetLatLngForUpload
            setAssetLocationLatitude={setUpload}
            setAssetLocationLongitude={setUpload}
            asset_location_latitude={asset_location_latitude}
            asset_location_longitude={asset_location_longitude}
            LatDataUserClickBeforeExitPage={asset_location_latitude}
            LongDataUserClickBeforeExitPage={asset_location_longitude}
          />
        </div>
      </FormSection>

      <FormSection title="พิกัด GPS">
        <div className="grid grid-cols-2 gap-3">
          {[
            { name: 'asset_location_latitude',  label: 'ละติจูด',  value: asset_location_latitude  },
            { name: 'asset_location_longitude', label: 'ลองจิจูด', value: asset_location_longitude },
          ].map(({ name, label, value }) => (
            <div key={name} className="flex flex-col gap-1">
              <span className="text-xs text-gray-400 text-center">{label}</span>
              <div className="relative">
                <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <FormInput value={value ?? ''} onChange={field(name)} type="number" placeholder="—" className="pl-8 text-center" />
              </div>
            </div>
          ))}
        </div>
      </FormSection>

      {submitError && <ErrorBanner message={submitError} />}
      {submitSuccess && <SuccessBanner message="บันทึกสำเร็จ กำลังพาไปหน้าหลัก..." />}

      {isUpdating && (
        <div className="flex justify-center py-2">
          <DotLoader color="#34bfd9" size={44} />
        </div>
      )}

      <FormButton onClick={handleSubmit} disabled={isUpdating || submitSuccess}>
        {isUpdating ? 'กำลังบันทึก...' : 'บันทึกการแก้ไข'}
      </FormButton>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full max-w-xl mx-auto px-4 pb-24">
        <div className="pt-6 pb-1">
          <h1 className="text-lg font-semibold text-gray-800">แก้ไข{config.title}</h1>
          <p className="text-xs text-gray-400 mt-0.5">แก้ไขข้อมูลแล้วกดบันทึกเพื่ออัปเดตประกาศ</p>
        </div>

        <div className="sticky top-0 z-20 bg-gray-50 pb-2 -mx-4 px-4">
          <StepBar current={pageNumber === 1.5 ? 1 : pageNumber} onGoTo={goToPage} />
        </div>

        {pageNumber === 1.5 && (
          <ImagesUploadEdit images={images} upload={upload} setUpload={setUpload} setPageNumber={setPageNumber} />
        )}
        {pageNumber === 1 && renderPage1()}
        {pageNumber === 2 && renderPage2()}
        {pageNumber === 3 && renderPage3()}
        {pageNumber === 4 && renderPage4()}
      </div>
    </div>
  );
}
